import clsx from 'clsx';
import React, { type PropsWithChildren, useCallback } from 'react';

type Option<T extends string> = { label: string; value: T };

type TabV2Props<T extends string> = Pick<Option<T>, 'value'> & {
  selected?: boolean;
  onClick: (value: T) => void;
};

const TabV2 = <T extends string>({
  children,
  value,
  onClick,
  selected = false,
}: PropsWithChildren<TabV2Props<T>>) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    className={clsx(
      'flex items-center',
      'px-f1 h-f4',
      'text-md',
      selected ? 'bg-main-200 text-main' : 'bg-canvas text-main-500',
      'hover:text-main hover:cursor-pointer',
      'last:border-r-[0.5px] border-l-[0.5px] border-main-300',
      'transition-colors',
      'box-border'
    )}
    data-testid={value}
  >
    {children}
  </button>
);

type TabsV2Props<T extends string> = {
  options: Option<T>[] | readonly Option<T>[];
  selectedValue?: T;
  onClick: (val: T) => void;
  extraClassNames?: string;
  'data-testid'?: string;
};

export const TabsV2 = <T extends string>({
  options,
  'data-testid': dataTestId,
  selectedValue = undefined,
  extraClassNames,
  onClick,
}: TabsV2Props<T>) => {
  const onClickHandler = useCallback(
    (value: T) => {
      onClick(value);
    },
    [onClick]
  );

  return (
    <div
      data-testid={dataTestId}
      className={clsx(
        'flex',
        'overflow-auto',
        'scrollbar-hide',
        'border-b-[0px] border-t-[0.5px] border-main-300',
        'box-border h-f4',
        'w-fit',
        'truncate',
        extraClassNames
      )}
    >
      {options.map((opt) => (
        <TabV2
          key={opt.value}
          value={opt.value}
          onClick={onClickHandler}
          selected={opt.value === selectedValue}
        >
          {opt.label}
        </TabV2>
      ))}
    </div>
  );
};
